import React, { useState, useEffect, useCallback } from 'react';
import { getCookie, setCookie } from '@pegasus/utils';
import { BirthdayVoucherBanner } from './components/banner';
import { BirthdayVoucherModal } from './components/modal';
import { useBirthdayVoucherData } from './hooks/useBirthdayVoucherData';
import { useBirthdayEligibility } from './hooks/useBirthdayEligibility';
import { BIRTHDAY_VOUCHER_MODAL_COOKIE, BIRTHDAY_VOUCHER_BANNER_COOKIE } from './utils';
import { ONE_DAY } from './constants';
/**
 * BirthdayVoucher - Self-contained component that manages birthday voucher modal and banner
 *
 * This component:
 * - Fetches customer incentives from Phoenix API internally
 * - Shows modal first (if eligible and not dismissed)
 * - Shows banner after modal is dismissed
 * - Manages all state internally
 *
 * Parent only needs to provide customer data from Redux
 */
export var BirthdayVoucher = function (_a) {
    var birthday = _a.birthday, customerName = _a.customerName, loyaltyTier = _a.loyaltyTier, loyaltyBenefits = _a.loyaltyBenefits, _b = _a.isMobileScreen, isMobileScreen = _b === void 0 ? false : _b, onShowFlashMessage = _a.onShowFlashMessage;
    // Fetch customer incentives from API
    var _c = useBirthdayVoucherData(), customerIncentive = _c.customerIncentive, loading = _c.loading;
    // Check if customer is eligible
    var isEligible = useBirthdayEligibility({ birthday: birthday, loyaltyBenefits: loyaltyBenefits });
    // State management
    var _d = useState(false), showModal = _d[0], setShowModal = _d[1];
    var _e = useState(false), showBanner = _e[0], setShowBanner = _e[1];
    // Format display name
    var displayName = customerName
        ? customerName.charAt(0).toUpperCase() + customerName.slice(1).toLowerCase()
        : '';
    // Initialize: check if should show modal or banner
    // Session logic: Modal shown in first session, banner in subsequent sessions
    useEffect(function () {
        if (!isEligible || !customerIncentive || loading)
            return;
        var modalCookie = getCookie(BIRTHDAY_VOUCHER_MODAL_COOKIE);
        var bannerCookie = getCookie(BIRTHDAY_VOUCHER_BANNER_COOKIE);
        if (!modalCookie) {
            // No modal cookie - show modal (first session)
            setShowModal(true);
        }
        else if (modalCookie === 'shown' && !bannerCookie) {
            // Modal was shown in previous session - show banner now
            setShowBanner(true);
        }
    }, [isEligible, customerIncentive, loading]);
    // Handle modal close - don't show banner in same session
    var handleCloseModal = useCallback(function () {
        setShowModal(false);
        // Banner will show in next session only (not in current session)
        setCookie(BIRTHDAY_VOUCHER_MODAL_COOKIE, 'shown', Date.now() + ONE_DAY * 3, '/');
    }, []);
    // Handle banner visibility change
    var handleBannerVisibilityChange = useCallback(function (visible) {
        setShowBanner(visible);
    }, []);
    // Don't render anything if not eligible or still loading
    if (!isEligible || loading) {
        return null;
    }
    return (React.createElement(React.Fragment, null,
        showBanner && customerIncentive && (React.createElement(BirthdayVoucherBanner, { onVisibilityChange: handleBannerVisibilityChange, customerIncentive: customerIncentive, displayName: displayName, tier: loyaltyTier, isMobileScreen: isMobileScreen, onShowFlashMessage: onShowFlashMessage })),
        customerIncentive && (React.createElement(BirthdayVoucherModal, { open: showModal, onClose: handleCloseModal, customerName: displayName, customerIncentive: customerIncentive, tier: loyaltyTier }))));
};
